import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { realtime } from '../services/realtime';
import { ensureFreshToken } from '../services/api';
import { setSignOutReason } from '../services/sessionEnd';
import { useAuth } from './AuthContext';

export const RealtimeContext = createContext(null);

export function RealtimeProvider({ children }) {
  const { isAuthenticated, logout } = useAuth();
  const [connected, setConnected] = useState(false);
  const connectListeners = useRef(new Set());

  useEffect(() => {
    if (!isAuthenticated) {
      realtime.stop();
      setConnected(false);
      return;
    }

    // Hand the socket a token PROVIDER, not a token value: it pulls (and refreshes
    // if needed) a valid JWT before each (re)connect, so the connection survives the
    // 30-min access-token expiry without the effect having to restart on rotation.
    realtime.start({ tokenProvider: ensureFreshToken });

    const offConnect = realtime.onConnect(() => {
      setConnected(true);
      connectListeners.current.forEach((fn) => fn());
    });
    const offError = realtime.onError(() => setConnected(false));

    // The session can't be authenticated and no refresh is possible — end it.
    const offAuthError = realtime.onAuthError(() => {
      setConnected(false);
      setSignOutReason('revoked');
      logout();
    });

    // Listen for server-pushed auth revocation.
    const offRevoke = realtime.subscribe('/user/queue/auth.revoked', () => {
      setSignOutReason('revoked');
      logout();
    });

    return () => {
      offConnect();
      offError();
      offAuthError();
      offRevoke();
      realtime.stop();
      setConnected(false);
    };
  }, [isAuthenticated, logout]);

  const value = {
    connected,
    subscribe: realtime.subscribe.bind(realtime),
    // Expose the underlying STOMP Client for transports that need raw publish/subscribe
    // (presenceTransport). This is null before the first connect.
    get stompClient() {
      return realtime.client;
    },
    addReconnectListener: (fn) => {
      connectListeners.current.add(fn);
      return () => connectListeners.current.delete(fn);
    },
  };

  return <RealtimeContext.Provider value={value}>{children}</RealtimeContext.Provider>;
}

export function useRealtime() {
  const ctx = useContext(RealtimeContext);
  if (!ctx) throw new Error('useRealtime must be used within RealtimeProvider');
  return ctx;
}
